/**
 * Papier Royal — Customily PDP Inherit
 *
 * Charge l'UNE PDP des deux produits cross-sell (business card ↔ enveloppe).
 * Si le client a personnalisé l'autre produit avant de naviguer ici, on lui
 * propose une notice « Reprendre votre personnalisation » avec bouton Appliquer.
 *
 * Flow :
 *   1. cart-companions.js → quand le drawer rend un companion avec sync=TRUE,
 *      il appelle PRCustomilyBridge.persistForPDP(companionHandle, design)
 *      → sessionStorage["pr_customily_inherit__<handle>"] = design serialized
 *
 *   2. Client clique sur l'image/titre du companion → navigation vers /products/<handle>
 *
 *   3. Sur la PDP, ce script s'exécute, détecte le sessionStorage, attend que
 *      le widget Customily soit prêt, et affiche la notice en haut.
 *
 *   4. Client clique « Appliquer » → injection programmatique des valeurs
 *      (text + font + color) dans les inputs Customily via dispatchEvent.
 *      Position laissée vide → client choisit lui-même.
 *
 *   5. Si Customily ne réagit pas à l'injection (DOM custom, Shadow DOM, etc.),
 *      la notice reste affichée comme guide visuel pour copier-coller à la main.
 */

(function () {
  'use strict';

  // Endpoint Cloudflare Worker qui rend les monogrammes en SVG vectoriel à partir
  // des .otf foundry-licensed stockés dans R2 (privés). Change vers le custom
  // domain quand mapping DNS configuré (ex: 'https://monogram.papierroyal.com').
  const MONOGRAM_RENDER_BASE = 'https://monogram-render.antonin-fe9.workers.dev';

  const config = Object.assign(
    {
      // GATE : on détecte UNIQUEMENT le bouton injecté par Customily quand le
      // produit est réellement personnalisable (template Customily configuré).
      // La notice ne s'affiche QUE si ce bouton est présent + visible.
      //
      // NE PAS gater sur le conteneur du bloc d'app (#shopify-block-..._customily_
      // product_personalizer_main_*, [id*="customily"], [class*="customily"]) :
      // ce bloc est rendu par le template sur CHAQUE produit (il contient juste
      // un <script> d'init), même non-configuré → la notice s'afficherait partout
      // (bug 2026-05-28/29). Le bouton, lui, n'est injecté que sur les produits
      // ayant un template Customily. Cohérent avec le handler apply (cf. plus bas)
      // qui clique '#customily-personalize-button, .cstm-open-btn'.
      customilySelectors: [
        '#customily-personalize-button',
        '.cstm-open-btn',
        '.customily-preview-btn',
      ],
      // ANCRAGE (positionnement uniquement, pas détection) : où insérer la
      // notice. Vise la zone ATC pour la placer juste au-dessus du bouton
      // PERSONALIZE / ATC. Fallback : avant le widget Customily, puis <main>.
      anchorSelectors: [
        '.product-form-buttons',
        '.product-form',
      ],
      maxWaitMs: 8000,
      pollIntervalMs: 250,
      debug: false,
    },
    window.PR_CUSTOMILY_PDP_INHERIT || {}
  );

  const log = (...args) => {
    if (config.debug) console.log('[PR PDP inherit]', ...args);
  };

  // ---------- Detect we're on a PDP ----------

  const handle = extractHandleFromUrl(window.location.pathname);
  if (!handle) {
    log('Not a PDP, skipping');
    return;
  }

  // ---------- Pre-fill quantity input from companion hint ----------
  // Posé par cart-companions.js wireCompanions quand le client clique sur
  // un companion. Si la PDP qui s'ouvre matche ce handle, on pre-remplit
  // le <input name="quantity"> avec la même qty que l'initiator. Ex:
  // 50 business cards → la PDP enveloppe s'ouvre avec qty=50.
  applyMatchingQtyHint(handle);

  // ---------- Read inherited design ----------

  // 1. D'abord essai per-handle (companion explicite cliqué dans le drawer)
  // 2. Fallback : clé suite globale (le client a juste personnalisé un autre
  //    produit Customily, n'importe lequel, et arrive sur cette PDP)
  let design = (window.PRCustomilyBridge?.consumeForPDP || readFallback)(handle);
  if (!design) {
    design = window.PRCustomilyBridge?.peekForSuite?.() || null;
    if (design) log('Found suite-wide design (fallback)', design);
  }
  if (!design) {
    log('No inherited design for', handle);
    return;
  }
  log('Inherited design loaded', design);

  // ---------- Wait for Customily widget then show notice ----------

  // Gate notice : si pas de widget Customily détecté → on est sur un produit
  // non-personnalisable (ex: gift card, accessoire), pas de notice.
  // Naturellement limite aux 4 catégories stationery (business-cards, letterhead,
  // envelope, correspondence) qui ont le configurateur Customily mounté.
  waitForCustomily(config.maxWaitMs, config.pollIntervalMs).then(async (widget) => {
    if (!widget) {
      log('Customily widget not detected — skipping notice (non-personalizable PDP)');
      return;
    }
    log('Customily widget found', widget);

    // Famille de perso : on ne propose le design hérité (monogramme) que sur les
    // produits dont la config Customily contient des polices SERIE (business
    // cards, enveloppes, correspondence, letterhead). Les carnets sont
    // personnalisables mais avec une perso "initiales" (pas de SERIE) → pas de
    // notice. Bug 2026-05-29 : la notice s'affichait sur les carnets.
    const monogram = await isMonogramCompatible(handle);
    if (!monogram) {
      log('Product not monogram-compatible (no SERIE font) — skipping notice');
      return;
    }

    injectNotice(widget, design);
  });

  // ============================================================
  // Helpers
  // ============================================================

  function extractHandleFromUrl(pathname) {
    const m = pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /**
   * Applique la matching qty hint posée par cart-companions.js (sessionStorage).
   * Stratégie adaptative selon présence d'un qty input visible sur la PDP :
   *
   * - PDP avec qty input visible (ex: business-cards) :
   *     Pre-fill l'input avec target. STOP. Pas de wrapper submit/fetch.
   *     L'user peut overrider à la main → on respecte. Bug 2026-05-21 fix :
   *     auparavant on forçait qty>=target en capture-phase, ce qui écrasait
   *     un choix explicite user (1 → 50).
   *
   * - PDP SANS qty input visible (ex: envelope) :
   *     Install submit interceptor + fetch wrapper qui forcent quantity=target
   *     dans l'ATC payload. C'est l'UNIQUE moyen de set la qty puisque l'UI
   *     n'expose rien.
   *
   * Clear le hint sur cart:update (= ATC réussi).
   */
  async function applyMatchingQtyHint(h) {
    let raw;
    try {
      raw = sessionStorage.getItem(`pr_companion_qty__${h}`);
    } catch {
      return;
    }
    if (!raw) return;

    // Format: {qty, initiator} JSON OU legacy plain number string. On parse les
    // deux, mais seul le JSON déclenche la validation cart (qui évite les hints
    // stales d'un ancien flow où l'initiator n'est plus dans le panier).
    let target, initiator = null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        target = parseInt(parsed.qty, 10);
        initiator = parsed.initiator || null;
      } else {
        target = parseInt(raw, 10);
      }
    } catch {
      target = parseInt(raw, 10);
    }
    if (!target || target <= 1) return;

    // Validation : si on connaît l'initiator (BC handle), on vérifie qu'il est
    // toujours dans le cart. Sinon hint stale → discard. Évite de forcer qty=50
    // sur un envelope ATC quand l'user vient directement sur la PDP sans flow drawer.
    if (initiator) {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const stillInCart = cart.items?.some((item) => item.handle === initiator);
        if (!stillInCart) {
          try { sessionStorage.removeItem(`pr_companion_qty__${h}`); } catch {}
          log('stale hint discarded — initiator', initiator, 'not in cart');
          return;
        }
      } catch {
        // Fail-open : si fetch cart casse, on continue avec la hint.
      }
    }

    log('matching qty hint target:', target, 'initiator:', initiator || '(legacy, unvalidated)');

    const findVisibleQtyInput = () => {
      const candidates = document.querySelectorAll('quantity-selector-component input[name="quantity"], input[name="quantity"]');
      for (const el of candidates) {
        if (el.closest('cart-quantity-selector-component')) continue; // skip cart line qty
        if (el.offsetParent === null) continue; // not visible
        return el;
      }
      return null;
    };

    // Logique adaptive : SEULES les PDPs sans qty input visible (envelope-style,
    // ATC headless) reçoivent les wrappers force-qty. Les PDPs avec qty input
    // (BC) — on NE PRE-FILL PAS, on ne wrappe pas. Raison :
    //   Bug compounding observé 2026-05-21 : pre-fill BC qty input avec hint
    //   provenant d'un envelope ATC précédent. User click ATC sans toucher
    //   → BC qty merge avec line existante (1→51), matchingQty re-calc plus
    //   grande, drawer suggère plus, boucle exponentielle (cart à 5105 BC).
    // Mieux : laisser l'user choisir manuellement la qty sur BC, seul l'envelope
    // ATC depuis drawer reçoit du forcing (cas use case original).
    let attempts = 0;
    const poll = setInterval(() => {
      const input = findVisibleQtyInput();
      if (input) {
        clearInterval(poll);
        log('visible qty input found — skipping pre-fill (avoid compound loop)');
        // Clear le hint immédiatement : il est pour un flow envelope, pas BC.
        try { sessionStorage.removeItem(`pr_companion_qty__${h}`); } catch {}
      } else if (++attempts > 20) {
        clearInterval(poll);
        log('no visible qty input — installing headless wrappers');
        installHeadlessWrappers();
      }
    }, 150);

    function installHeadlessWrappers() {
      const onSubmit = (e) => {
        const form = e.target;
        if (!form || !form.action || !form.action.includes('/cart/add')) return;
        let input = form.querySelector('input[name="quantity"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'quantity';
          form.appendChild(input);
        }
        if (Number(input.value) < target) {
          input.value = String(target);
          log('form submit qty forced to', target);
        }
      };
      document.addEventListener('submit', onSubmit, true);

      // [LOT A — 2026-07-06] Wrapper window.fetch RETIRÉ. Forçage quantité conservé via le
      // listener 'submit' ci-dessus (event-based). Forçage AJAX headless → Lot B.

      document.addEventListener('cart:update', () => {
        try { sessionStorage.removeItem(`pr_companion_qty__${h}`); } catch {}
        document.removeEventListener('submit', onSubmit, true);
      }, { once: true });
    }
  }

  function readFallback(h) {
    // Fallback si PRCustomilyBridge n'est pas chargé encore
    try {
      const raw = sessionStorage.getItem(`pr_customily_inherit__${h}`);
      if (!raw) return null;
      sessionStorage.removeItem(`pr_customily_inherit__${h}`);
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Vérifie que le produit appartient à la famille MONOGRAMME en inspectant sa
   * config Customily (endpoint settings/unified, déjà préchargé en <link preload>
   * par le bloc d'app Customily). Critère : présence de polices "SERIE". Les
   * carnets (perso initiales) n'en ont pas ; business-cards / envelopes /
   * correspondence / letterhead oui. Résultat caché par handle en sessionStorage
   * (1 fetch par PDP). Fail-open : si le check échoue (réseau / CORS), on
   * autorise la notice plutôt que de casser le flow cross-sell.
   */
  async function isMonogramCompatible(h) {
    const cacheKey = `pr_monogram_ok__${h}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached !== null) return cached === '1';
    } catch {}

    let ok = true; // fail-open
    try {
      const shop = window.Shopify?.shop;
      const product = await fetch(`/products/${h}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const pid = product?.id;
      if (shop && pid) {
        const url = `https://sh.customily.com/api/settings/unified/${encodeURIComponent(h)}?shop=${encodeURIComponent(shop)}&productId=${pid}`;
        const cfg = await fetch(url).then((r) => r.text());
        ok = /serie/i.test(cfg);
        log('monogram compatibility', h, '→', ok);
      }
    } catch (e) {
      log('monogram check failed — fail-open', e);
      ok = true;
    }

    try { sessionStorage.setItem(cacheKey, ok ? '1' : '0'); } catch {}
    return ok;
  }

  function waitForCustomily(maxMs, intervalMs) {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        for (const sel of config.customilySelectors) {
          const el = document.querySelector(sel);
          if (el && el.offsetParent !== null) {
            // Detected + visible
            resolve(el);
            return;
          }
        }
        if (Date.now() - start > maxMs) {
          resolve(null);
          return;
        }
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  // ---------- UI ----------

  function injectNotice(widget, design) {
    const notice = document.createElement('aside');
    notice.className = 'pr-inherit-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');

    const text = String(design.primaryText || design.text || '').toUpperCase().slice(0, 4);
    const font = String(design.font || '').trim();
    const color = String(design.color || '').trim();
    const altParts = [text, font, color].filter(Boolean).join(', ');

    const L = Object.assign(
      {
        title: 'Ajoutez votre design',
        continueLabel: 'Continuer',
        dismissLabel: 'Ignorer'
      },
      window.PR_PDP_INHERIT_LABELS || {}
    );

    // Preview = Cloudflare Worker qui rend le monogramme en SVG vectoriel
    // (text + font OTF côté serveur + color). Avantages :
    //   - Vraie police SERIE (rendering opentype.js côté Worker)
    //   - Fonts JAMAIS shipped au client (.otf stockés en R2 privé)
    //   - SVG natif cropé au monogramme (viewBox calculé du bbox du path)
    //   - Edge cache Cloudflare 1 an (clé d'URL déterministe)
    //   - Pas de pixel analysis, pas de bordures de carte, pas de CORS
    // Si pas de slug font matchable (ex: police custom hors registry), on
    // fall-back sur un SVG initiales générique.
    const colorHex = resolveColorHex(color);
    const slug = font ? font.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '';
    const workerUrl = slug && text
      ? `${MONOGRAM_RENDER_BASE}/r?text=${encodeURIComponent(text)}&font=${slug}&color=${colorHex.replace('#', '%23')}&size=256`
      : '';
    const fontSize = text.length > 2 ? 36 : 52;
    const fallbackSvg = text
      ? `<svg class="pr-inherit-notice__preview-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-label="${escapeHtml(altParts)}">
           <text x="50" y="50" text-anchor="middle" dominant-baseline="central" fill="${colorHex}" font-size="${fontSize}" font-family="var(--font-h2--family, var(--font-heading--family, 'Bodoni Moda', 'Cormorant Garamond', serif))" font-weight="400" letter-spacing="2">${escapeHtml(text)}</text>
         </svg>`
      : '';

    // <img onerror> → si le Worker répond 404 (slug font inconnu), on remplace
    // le DOM du slot par le SVG fallback inline.
    const previewMarkup = workerUrl
      ? `<img class="pr-inherit-notice__preview-img" src="${escapeHtml(workerUrl)}" alt="${escapeHtml(altParts)}" loading="lazy" decoding="async" onerror="this.closest('.pr-inherit-notice__preview').innerHTML=this.dataset.fallback" data-fallback="${escapeHtml(fallbackSvg)}">`
      : fallbackSvg;

    notice.innerHTML = `
      <div class="pr-inherit-notice__head">
        <p class="pr-inherit-notice__label">${escapeHtml(L.title)}</p>
        <button type="button" class="pr-inherit-notice__close" aria-label="Fermer">×</button>
      </div>
      ${previewMarkup ? `<figure class="pr-inherit-notice__preview">${previewMarkup}</figure>` : ''}
      ${font || color ? `<p class="pr-inherit-notice__caption">${escapeHtml([font, color].filter(Boolean).join(' · '))}</p>` : ''}
      <div class="pr-inherit-notice__actions">
        <button type="button" class="pr-inherit-notice__apply">${escapeHtml(L.continueLabel)}</button>
        <button type="button" class="pr-inherit-notice__dismiss">${escapeHtml(L.dismissLabel)}</button>
      </div>
    `;

    // Positionnement : on cherche d'abord un anchor près de la zone ATC
    // (.product-form-buttons / .product-form), sinon on retombe sur le widget
    // Customily détecté, sinon en haut du <main>.
    let anchor = null;
    for (const sel of config.anchorSelectors) {
      const el = document.querySelector(sel);
      if (el && el.offsetParent !== null) {
        anchor = el;
        break;
      }
    }
    anchor = anchor || widget;
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(notice, anchor);
    } else {
      const fallback = document.querySelector('main, .main-content, body');
      fallback?.insertBefore(notice, fallback.firstChild);
    }

    // Handlers — Customily n'expose pas d'API JS publique (uniquement
    // window.customily.shopSettings, sans méthodes). Donc on n'auto-apply pas
    // dans le canvas Customily ; on ouvre la modale du configurateur et
    // laisse le client reporter les valeurs visibles dans la notice
    // (Monogramme / Police / Encre) à la main. Les valeurs restent affichées
    // pendant qu'il personnalise.
    notice.querySelector('.pr-inherit-notice__apply')?.addEventListener('click', () => {
      // CLEAR Customily's localStorage state for this product AVANT d'ouvrir
      // la modale. Sinon Customily restore la perso précédente (clé
      // 'customily-{productId}') et écrase notre injection. On ne clear que
      // les keys d'état + hash, on garde les thumbs (cache d'images).
      try {
        Object.keys(localStorage).forEach((k) => {
          // Match : customily-NNNN (state) OR customily-persionalization-hash-NNNN
          // (current hash). Skip -initial (les hashs de référence) et -thumbs-*.
          if (/^customily-\d+$/.test(k)) {
            localStorage.removeItem(k);
          } else if (/^customily-persionalization-hash-\d+$/.test(k)) {
            localStorage.removeItem(k);
          }
        });
        log('Cleared Customily restore state to allow injection');
      } catch (e) {
        log('Failed to clear Customily localStorage', e);
      }

      const personalizeBtn = document.querySelector('#customily-personalize-button, .cstm-open-btn');
      if (personalizeBtn) {
        personalizeBtn.click();
      }
      const text = design.primaryText || design.text || '';
      const font = design.font || '';
      const color = design.color || '';

      // Stratégie hybride (text via Vue, font/color via click natif) :
      //   1. Text   → mute v2.engraverTexts[*].value (canvas) + textOpt.value (cart property)
      //   2. Font   → click le <label> du swatch (alt="SERIE 122") — Customily
      //               gère natif font asset + layer visibility + canvas + cart prop
      //   3. Color  → ouvre dropdown puis click <li.csh-dropdown-item> ciblé
      //               Customily gère natif color + canvas + cart prop
      //
      // Pourquoi pas tout en mutation Vue : font registry interne de Customily
      // log "No font at position N" si on set juste fontOpt.value — le canvas
      // garde la font par défaut. Les clicks natifs déclenchent la cascade
      // complète (font load + layer swap + render). Confirmé live 2026-05-19.
      //
      // 1500ms : laisse Customily mount son widget + restorer son state.
      setTimeout(async () => {
        const modal = document.querySelector('.customily-modal-container');
        if (!modal) {
          log('Customily modal not found after 1500ms');
          return;
        }
        const v2 = [...modal.querySelectorAll('*')].find((el) => el.__vue__)?.__vue__;
        if (!v2) {
          log('Customily Vue instance not found');
          return;
        }
        const opts = v2.sets?.[0]?.options || [];
        const r = { text: false, font: false, color: false };

        // 1. Text — Vue mutation + DOM input dispatch (force canvas re-render).
        // La mutation Vue option.value met à jour le cart property + le
        // engraverTexts layers met à jour le canvas. MAIS sur certains cas
        // (Customily restore state, debounce), le canvas garde l'ancienne
        // valeur. Solution : on dispatch aussi un 'input' event sur l'input
        // DOM réel — ça déclenche Customily processing comme si user typait.
        if (text) {
          const upper = String(text).toUpperCase();
          const textOpt = opts.find((o) => o.type === 'Text Input');
          if (textOpt) {
            try {
              textOpt.value = upper;
              r.text = true;
            } catch (e) {
              log('Text option mutation failed', e);
            }
            // DOM input dispatch — utilise le native setter pour bypass Vue
            // wrapper et trigger input event que Customily écoute.
            const textInputs = modal.querySelectorAll(`[opt-id="${textOpt.id}"] input[type="text"], [opt-id="${textOpt.id}"] input:not([type])`);
            const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
            textInputs.forEach((inp) => {
              try {
                if (nativeSetter) nativeSetter.call(inp, upper);
                else inp.value = upper;
                inp.dispatchEvent(new Event('input', { bubbles: true }));
                inp.dispatchEvent(new Event('change', { bubbles: true }));
              } catch (e) {
                log('Text DOM dispatch failed', e);
              }
            });
          }
          // engraverTexts is keyed by layer ID — mute all layers so any
          // visible-after-font-swap layer shows the text.
          const layers = v2.engraverTexts;
          if (layers && typeof layers === 'object') {
            Object.values(layers).forEach((layer) => {
              if (layer && typeof layer === 'object' && 'value' in layer) {
                try { layer.value = upper; } catch {}
              }
            });
          }
          log('Text applied:', upper);
        }

        // 2. Font — click le label swatch dont l'<img alt="..."> matche.
        // Sur la carte, alt === "SERIE 125" exact. Sur l'enveloppe le naming peut
        // différer (accents/espaces) OU la police est un Dropdown Swatch (comme la
        // couleur) plutôt qu'un swatch <label>. On garde le match exact en 1er
        // (chemin carte inchangé), puis fallbacks NORMALISÉS/dropdown (additif).
        if (font) {
          const norm = (s) => String(s || '')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toUpperCase().replace(/[^A-Z0-9]/g, '');
          const target = String(font).trim().toUpperCase();
          const targetN = norm(font);
          const labels = [...modal.querySelectorAll('label[for*="cl-value-id-"]')];
          let fontLabel =
            labels.find((lbl) => (lbl.querySelector('img')?.alt || '').trim().toUpperCase() === target) ||
            labels.find((lbl) => norm(lbl.querySelector('img')?.alt) === targetN) ||
            labels.find((lbl) => norm(lbl.textContent) === targetN);
          if (fontLabel) {
            try { fontLabel.click(); r.font = true; log('Font clicked (swatch):', target); }
            catch (e) { log('Font click failed', e); }
          } else {
            // Fallback : option Vue (swatch/dropdown/radio) dont une value matche la police.
            const fontOpt = opts.find(
              (o) =>
                /swatch|dropdown|image|font|radio/i.test(o.type || '') &&
                o.type !== 'Text Input' &&
                o.values?.some((v) => norm(v.value) === targetN || norm(v.name) === targetN)
            );
            if (fontOpt) {
              const optDom = modal.querySelector(`[opt-id="${fontOpt.id}"]`);
              const trig = optDom?.querySelector('.csh-dropdown-input-container');
              if (trig) {
                try { trig.click(); } catch {}
                await new Promise((res) => setTimeout(res, 350));
                let item =
                  [...(optDom.querySelectorAll('li.csh-dropdown-item') || [])].find((li) => norm(li.textContent) === targetN) ||
                  [...document.querySelectorAll('li.csh-dropdown-item')].find((li) => norm(li.textContent) === targetN && li.offsetParent !== null);
                if (item) { try { item.click(); r.font = true; log('Font clicked (dropdown):', target); } catch {} }
                else log('Font LI not found for', target);
              } else if (optDom) {
                const lbl = [...optDom.querySelectorAll('label')].find(
                  (l) => norm(l.querySelector('img')?.alt) === targetN || norm(l.textContent) === targetN
                );
                if (lbl) { try { lbl.click(); r.font = true; log('Font clicked (opt swatch):', target); } catch {} }
                else log('Font swatch not found in opt for', target);
              }
            } else {
              log('Font not matched for', target, '— opt types:', opts.map((o) => o.type));
            }
          }
        }

        // Laisse Customily traiter le font swap (asset load) avant la couleur
        await new Promise((res) => setTimeout(res, 300));

        // 3. Color — Vue identifie le bon dropdown (par values), DOM open + click LI
        // Plusieurs Dropdown Swatch peuvent coexister (couleur monogramme ID=3 +
        // couleur papier ID=7 + autres). On filtre via Vue : le dropdown qui A
        // notre target dans ses values est forcément le bon (palettes distinctes
        // : monogramme=21 encres, papier=8 papiers, etc.). Puis on scope l'ouverture
        // + le click LI à `<div opt-id="${o.id}">` pour pas taper le mauvais.
        if (color) {
          const target = String(color).trim().toLowerCase();
          const colorOpt = opts.find(
            (o) =>
              o.type === 'Dropdown Swatch' &&
              o.values?.some((v) => String(v.value).trim().toLowerCase() === target)
          );
          if (colorOpt) {
            const optDom = modal.querySelector(`[opt-id="${colorOpt.id}"]`);
            const trig = optDom?.querySelector('.csh-dropdown-input-container');
            if (trig) {
              try { trig.click(); } catch {}
              await new Promise((res) => setTimeout(res, 350));
              // LI peut être scopé à optDom ou portalé ; on essaie scoped d'abord
              let item = [...optDom.querySelectorAll('li.csh-dropdown-item')].find(
                (li) => (li.textContent || '').trim().toLowerCase() === target
              );
              // Fallback : LI visible globalement (seul ce dropdown vient d'ouvrir)
              if (!item) {
                item = [...document.querySelectorAll('li.csh-dropdown-item')].find(
                  (li) =>
                    (li.textContent || '').trim().toLowerCase() === target &&
                    li.offsetParent !== null
                );
              }
              if (item) {
                try {
                  item.click();
                  r.color = true;
                  log('Color clicked:', color, '(opt-id=' + colorOpt.id + ')');
                } catch (e) {
                  log('Color click failed', e);
                }
              } else {
                log('Color LI not found in opt-id=' + colorOpt.id + ' after open');
              }
            } else {
              log('Color dropdown trigger not found for opt-id=' + colorOpt.id);
            }
          } else {
            log('No Dropdown Swatch option contains color value:', color);
          }
        }

        // Re-render fiable : Customily ne rafraîchit PAS le canvas après le swap de
        // police (asset chargé en async). Le seul déclencheur fiable = fermer puis
        // rouvrir le modale (confirmé manuellement). On l'automatise ici après
        // injection : l'état (police correcte) est déjà persisté, donc la ré-ouverture
        // re-render avec le bon monogramme. Léger flash, mais rendu garanti instantané.
        // On ne le fait QUE si la police a matché (r.font) — sinon rien à re-render.
        if (r.font) {
          await new Promise((res) => setTimeout(res, 500));
          // findClose : même heuristique que customily-ux.js (bouton "close" du modale).
          let closeBtn = modal.querySelector('[class*="close" i], [id*="close" i]');
          if (!closeBtn) {
            closeBtn = [...modal.querySelectorAll('button, a, span, div, i')].find((el) => {
              const t = (el.getAttribute('aria-label') || el.title || el.textContent || '').trim().toLowerCase();
              return t === '×' || t === 'x' || t === 'close' || t === 'fermer';
            });
          }
          if (closeBtn) {
            try { closeBtn.click(); } catch {}
            await new Promise((res) => setTimeout(res, 500));
            const reopen = document.querySelector('#customily-personalize-button, .cstm-open-btn');
            try { if (reopen) reopen.click(); } catch {}
            log('Auto close/reopen done — font render forced');
          } else {
            log('Close button not found — cannot auto-reopen for font render');
          }
        }

        log('Customily inject result', r);
      }, 1500);
      // Notice reste visible — aide-mémoire au cas où une option n'aurait
      // pas matché (template Customily différent, etc.).
    });

    notice.querySelector('.pr-inherit-notice__dismiss')?.addEventListener('click', () => notice.remove());
    notice.querySelector('.pr-inherit-notice__close')?.addEventListener('click', () => notice.remove());
  }

  // Map des couleurs Customily (encre/text) → hex visuel approx pour le render
  // SVG. Hex best-effort, pas les valeurs exactes Customily (pas d'API). Si une
  // couleur captée n'est pas listée, fallback ink noir (#1a1a1a).
  const COLOR_MAP = {
    // Blues
    'navy blue': '#1f2e4a',
    'peacock blue': '#1c5a7e',
    'baby blue': '#a8c7e0',
    'colonial blue': '#3a6378',
    'french blue': '#5e7fa3',
    'azure blue': '#1f6bb8',
    'royal blue': '#1f3aa0',
    // Reds + warms
    'christmas red': '#a82a2a',
    'crimson red': '#a01b1f',
    'poinsettia red': '#c12222',
    'fire red': '#d63a1d',
    'burgundy': '#6a1a2a',
    'magenta': '#a83478',
    'pink': '#d68aab',
    'orange': '#d9692a',
    // Greens
    'green': '#3a6b3a',
    'emerald green': '#2a8a55',
    'forest green': '#2a4a2a',
    'olive': '#7a7a3a',
    'turquoise': '#3a8a90',
    // Browns + neutrals
    'medium brown': '#7a5a3a',
    'dark brown': '#4a3a2a',
    'tan': '#c8a878',
    'quaker gray': '#6a6a6a',
    'charcoal': '#2a2a2a',
    'silver': '#a8a8a8',
    // Blacks
    'black': '#1a1a1a',
    'gloss black': '#0a0a0a',
    'matte black': '#1a1a1a',
    // Lights + metallics
    'white': '#fafafa',
    'cream': '#f5edd8',
    'ivory': '#f8f0d8',
    'gold': '#b08d57',
    'rose gold': '#c08878',
    // Purples + yellows
    'purple': '#6a3a82',
    'lavender': '#a8a0c8',
    'yellow': '#dab23a',
    'mustard': '#c89a2a'
  };

  function resolveColorHex(name) {
    if (!name) return '#1a1a1a';
    const key = String(name).toLowerCase().trim();
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    if (/^#[0-9a-f]{3,8}$/i.test(name)) return name;
    return '#1a1a1a';
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

})();
// cache-bust: 2026-05-29-gate-button-plus-serie-monogram-family
